import React, { useMemo, useState } from 'react'
import { useProjectContext } from '../../context/ProjectContext'
import { dmcColors } from '../../data/dmcColors'

const inferFamily = (name = '') => {
  const n = String(name).toLowerCase()
  if (n.includes('green')) return 'Green'
  if (n.includes('blue')) return 'Blue'
  if (n.includes('red')) return 'Red'
  if (n.includes('pink')) return 'Pink'
  if (n.includes('purple') || n.includes('violet')) return 'Purple'
  if (n.includes('yellow') || n.includes('gold')) return 'Yellow'
  if (n.includes('orange')) return 'Orange'
  if (n.includes('brown') || n.includes('tan') || n.includes('beige')) return 'Brown'
  if (n.includes('gray') || n.includes('grey') || n.includes('black') || n.includes('white')) return 'Neutral'
  return ''
}

const normalize = (v) => String(v ?? '').toLowerCase()

const ColorPalette = () => {
  const {
    project,
    removeColorFromPalette,
    addColorToPalette,
    setSelectedColor,
    selectedColor,
    recentThreads,
    threadType,
    setThreadType,
    strandsUsed,
    setStrandsUsed,
    perleSize,
    setPerleSize,
  } = useProjectContext()

  const [searchTerm, setSearchTerm] = useState('')

  const term = normalize(searchTerm).trim()

  const paletteCodes = useMemo(() => {
    return new Set((project?.palette || []).map((c) => String(c.code)))
  }, [project?.palette])

  const filteredPalette = useMemo(() => {
    const list = project?.palette || []
    if (!term) return list

    return list.filter((color) => {
      const code = normalize(color.code)
      const name = normalize(color.name)
      const family = normalize(color.family || inferFamily(color.name))
      return code.includes(term) || name.includes(term) || family.includes(term)
    })
  }, [project?.palette, term])

  const filteredDmc = useMemo(() => {
    if (!term) return []
    const list = Array.isArray(dmcColors) ? dmcColors : (dmcColors?.default || [])
    return list
      .filter((color) => {
        const code = normalize(color.code)
        const name = normalize(color.name)
        const family = normalize(color.family || inferFamily(color.name))
        return code.includes(term) || name.includes(term) || family.includes(term)
      })
      .slice(0, 60)
  }, [term])

  const hasPalette = (project?.palette || []).length > 0

  return (
    <div className="color-palette" style={{ width: '100%', minWidth: 0, maxWidth: '100%', overflowX: 'hidden' }}>
      <h2>Color Palette</h2>

      <div className="active-thread">
        <div className="active-thread-label">Active Thread</div>
        <div className="active-thread-display" style={{ minWidth: 0 }}>
          <div
            className="active-color-box"
            style={{ backgroundColor: selectedColor?.hex || '#ffffff' }}
          />
          <div className="active-color-info">
            <div className="active-color-code">
              {selectedColor?.code ? `DMC ${selectedColor.code}` : 'No color selected'}
            </div>
            <div className="active-color-name">
              {selectedColor?.name || ''}
            </div>
          </div>
        </div>
      </div>

      <div className="thread-settings">
  <div className="thread-settings-title">Thread Settings</div>

  <div className="thread-type-group">
    <label className="thread-radio">
      <input
        type="radio"
        name="threadType"
        value="stranded"
        checked={threadType === 'stranded'}
        onChange={() => setThreadType('stranded')}
      />
      <span>Stranded Floss</span>
    </label>

    <label className="thread-radio">
      <input
        type="radio"
        name="threadType"
        value="perle"
        checked={threadType === 'perle'}
        onChange={() => setThreadType('perle')}
      />
      <span>Pearl Cotton</span>
    </label>
  </div>

  {threadType === 'stranded' ? (
    <div className="thread-setting-row" style={{ minWidth: 0 }}>
      <label htmlFor="strandsUsed" className="thread-setting-label">
        Strands Used
      </label>
      <select
        id="strandsUsed"
        className="thread-setting-select"
        value={strandsUsed}
        onChange={(e) => setStrandsUsed(Number(e.target.value))}
      >
        <option value={2}>2</option>
        <option value={3}>3</option>
        <option value={6}>6</option>
      </select>
    </div>
  ) : (
    <div className="thread-setting-row" style={{ minWidth: 0 }}>
      <label htmlFor="perleSize" className="thread-setting-label">
        Perle Size
      </label>
      <select
        id="perleSize"
        className="thread-setting-select"
        value={perleSize}
        onChange={(e) => setPerleSize(Number(e.target.value))}
      >
        <option value={3}>3</option>
        <option value={5}>5</option>
        <option value={8}>8</option>
        <option value={12}>12</option>
      </select>
    </div>
  )}
</div>

      <div className="palette-search-row" style={{ minWidth: 0 }}>
        <input
          type="text"
          className="palette-search"
          placeholder="Search DMC"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button
          type="button"
          className="palette-clear ui-control"
          onClick={() => setSearchTerm('')}
          disabled={!searchTerm}
          title="Clear search"
        >
          Clear
        </button>
      </div>

      {searchTerm && (
        <div className="palette-results">
          <span>Your Palette: {filteredPalette.length}</span>
          <span>DMC Library: {filteredDmc.length}</span>
        </div>
      )}

      {recentThreads?.length > 0 && (
        <>
          <h3 className="palette-section-title">Recent Threads</h3>
          <div className="recent-threads-list">
            {recentThreads.map((color) => (
              <button
                key={`recent-${color.code}`}
                type="button"
                className="recent-thread-chip"
                onClick={() => setSelectedColor(color)}
                title={color.code}
              >
                <span
                  className="recent-thread-swatch"
                  style={{ backgroundColor: color.hex }}
                />
                <span className="recent-thread-code">{color.code}</span>
              </button>
            ))}
          </div>
        </>
      )}

      <h3 className="palette-section-title">Your Palette</h3>

      {!hasPalette ? (
        <p className="palette-hint">
          No colors added yet. Use the color picker in the toolbar or search the DMC Library below.
        </p>
      ) : filteredPalette.length === 0 ? (
        <div className="palette-empty">
          No matches in <strong>Your Palette</strong>. Try the <strong>DMC Library</strong> below.&nbsp;
          <button type="button" className="linklike" onClick={() => setSearchTerm('')}>
            Clear search
          </button>
        </div>
      ) : (
        <div className="palette-list" style={{ width: '100%', minWidth: 0, maxWidth: '100%', overflowX: 'hidden' }}>
          {filteredPalette.map((color) => (
            <div
              key={color.code}
              className={`palette-item ${selectedColor?.code === color.code ? 'active' : ''}`}
              onClick={() => setSelectedColor(color)}
              style={{ minWidth: 0 }}
            >
              <span className="color-swatch" style={{ backgroundColor: color.hex }} />

              <div className="palette-text">
                <div className="palette-code">{color.code}</div>
                <div className="palette-name">{color.name}</div>
              </div>

              <button
                type="button"
                className="palette-remove ui-control"
                onClick={(e) => {
                  e.stopPropagation()
                  removeColorFromPalette(color.code)
                }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      <h3 className="palette-section-title">DMC Library</h3>

      {!term ? (
        <p className="palette-hint">
          Type in the search box to find DMC colors by number, name, or family.
        </p>
      ) : filteredDmc.length === 0 ? (
        <div className="palette-empty">No DMC matches for “{searchTerm}”.</div>
      ) : (
        <div className="palette-list" style={{ width: '100%', minWidth: 0, maxWidth: '100%', overflowX: 'hidden' }}>
          {filteredDmc.map((color) => {
            const alreadyAdded = paletteCodes.has(String(color.code))
            return (
              <div key={`dmc-${color.code}`} className="palette-item" style={{ minWidth: 0 }}>
                <span className="color-swatch" style={{ backgroundColor: color.hex }} />

                <div className="palette-text">
                  <div className="palette-code">{color.code}</div>
                  <div className="palette-name">{color.name}</div>
                </div>

                <button
                  type="button"
                  className="palette-add ui-control"
                  disabled={alreadyAdded}
                  onClick={() => addColorToPalette(color)}
                  title={alreadyAdded ? 'Already in your palette' : 'Add to palette'}
                >
                  {alreadyAdded ? 'Added' : 'Add'}
                </button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default ColorPalette